import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import { LoaderCircle } from 'lucide-react';
import type { ComponentProps } from 'react';
import { Slot } from 'radix-ui';
import { cn } from '@/lib/utils';

export const buttonVariants = cva(
  'focus-ring inline-flex shrink-0 select-none items-center justify-center gap-1.5 whitespace-nowrap rounded-sm border font-medium transition-colors duration-150 disabled:pointer-events-none disabled:opacity-50 aria-busy:cursor-progress [&_svg]:pointer-events-none [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        primary:
          'border-accent bg-accent text-accent-fg hover:border-accent-hover hover:bg-accent-hover',
        secondary: 'border-control-border bg-surface text-text hover:bg-hover',
        ghost: 'border-transparent text-text hover:bg-hover',
        subtle: 'border-transparent bg-sunken text-text hover:bg-hover',
        danger:
          'border-danger bg-danger text-danger-fg hover:border-danger-hover hover:bg-danger-hover',
        'danger-outline':
          'border-control-border bg-surface text-danger hover:border-danger hover:bg-danger-soft hover:text-danger-soft-fg',
        link: 'h-auto border-transparent px-0 text-accent-soft-fg underline-offset-4 hover:underline',
      },
      size: {
        sm: 'h-7 px-2.5 text-sm [&_svg]:size-3.5',
        md: 'h-8 px-3 text-sm [&_svg]:size-4',
        lg: 'h-10 px-4 text-base [&_svg]:size-4',
        icon: 'size-8 [&_svg]:size-4',
        'icon-sm': 'size-7 [&_svg]:size-4',
      },
    },
    defaultVariants: { variant: 'secondary', size: 'md' },
  },
);

export interface ButtonProps extends ComponentProps<'button'>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  /**
   * Shows a spinner and ignores presses (clicks, keyboard activation and implicit form submission) while true.
   * The button stays focusable: it is marked `aria-busy` and `aria-disabled` instead of `disabled`, so a focused
   * button keeps focus while its action runs. Not applied with `asChild`.
   */
  loading?: boolean;
}

export function Button({
  className,
  variant,
  size,
  asChild = false,
  loading = false,
  disabled,
  children,
  type,
  onClick,
  ...props
}: ButtonProps) {
  const classes = cn(buttonVariants({ variant, size }), className);
  if (asChild) {
    return (
      <Slot.Root className={classes} onClick={onClick} {...props}>
        {children}
      </Slot.Root>
    );
  }
  return (
    <button
      type={type ?? 'button'}
      className={classes}
      disabled={disabled}
      aria-disabled={(loading && !disabled) || undefined}
      aria-busy={loading || undefined}
      {...props}
      onClick={(event) => {
        if (loading) {
          event.preventDefault();
          return;
        }
        onClick?.(event);
      }}
    >
      {loading ? <LoaderCircle className="animate-spin" aria-hidden /> : null}
      {children}
    </button>
  );
}
